import { useState } from "react";
import {
  ArrowRight,
  Eye,
  Zap,
  CircleSlash,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import { useScrollReveal, useMouseTracking } from "@/hooks/use-scroll-reveal";
import QuizDialog from "@/components/QuizDialog";

const ARCHETYPES = [
  {
    code: "leon",
    name: "LEÓN",
    img: "https://customer-assets.emergentagent.com/job_strategic-evolution/artifacts/q43hc936_002-lion.png",
    tagline: "empuja y decide",
    color: "#FF6B4A",
  },
  {
    code: "lobo",
    name: "LOBO",
    img: "https://customer-assets.emergentagent.com/job_strategic-evolution/artifacts/q013w4ih_007-golden-wolf-1.png",
    tagline: "analiza y estructura",
    color: "#A78BFA",
  },
  {
    code: "halcon",
    name: "HALCÓN",
    img: "https://customer-assets.emergentagent.com/job_strategic-evolution/artifacts/btoarlhg_005-eagle-1.png",
    tagline: "ve y proyecta",
    color: "#29BC9B",
  },
  {
    code: "delfin",
    name: "DELFÍN",
    img: "https://customer-assets.emergentagent.com/job_strategic-evolution/artifacts/nztl5ndq_004-dolphin-1.png",
    tagline: "conecta y alinea",
    color: "#5BC0EB",
  },
];

const STAGES = [
  {
    n: "01",
    label: "Identificación",
    quote: "Así juego naturalmente.",
    desc: "El punto de partida. Reconoces tu estrategia inconsciente.",
    Icon: Eye,
  },
  {
    n: "02",
    label: "Conciencia",
    quote: "Mi fortaleza también puede jugar en mi contra.",
    desc: "El despertar estratégico. Ves la sombra de tu virtud.",
    Icon: Sparkles,
  },
  {
    n: "03",
    label: "Regulación",
    quote: "Necesito controlar mi reacción automática.",
    desc: "Dominio bajo presión. Eliges en vez de reaccionar.",
    Icon: CircleSlash,
  },
  {
    n: "04",
    label: "Evolución",
    quote: "Ahora uso mi ventaja con intención y madurez.",
    desc: "El estadio superior. Juegas mejor sin dejar de ser tú.",
    Icon: TrendingUp,
  },
];

const STATES = [
  "te aceleras demasiado",
  "te bloqueas pensando",
  "te desconectas emocionalmente",
  "o evitas tensión para mantener armonía",
];

export default function Landing() {
  const ref = useScrollReveal();
  useMouseTracking();
  const [quizOpen, setQuizOpen] = useState(false);

  return (
    <div ref={ref} className="bg-[#050505] text-white min-h-screen relative">
      {/* NAVIGATION */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/5"
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="font-display text-lg tracking-tighter">PLAY YOUR GAME™</div>
          <div className="hidden md:flex space-x-10 text-[10px] uppercase tracking-[0.3em] font-medium text-white/50">
            <a href="#camino" className="hover:text-white transition-colors" data-testid="nav-camino">Evolución</a>
            <a href="#avatares" className="hover:text-white transition-colors" data-testid="nav-avatares">Sistema</a>
            <a href="#filosofia" className="hover:text-white transition-colors" data-testid="nav-filosofia">Filosofía</a>
          </div>
          <button
            onClick={() => setQuizOpen(true)}
            data-testid="nav-cta-btn"
            className="hidden md:inline-flex btn-teal !px-5 !py-2.5 !text-[10px]"
          >
            <Zap className="w-3 h-3" /> Iniciar test
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section
        className="relative min-h-[100vh] flex flex-col items-center justify-center px-6 pt-32 pb-24 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background image w/ heavy overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/9665193/pexels-photo-9665193.jpeg"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-[#050505]" />
          <div className="absolute inset-0 grid-pattern opacity-50" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#29BC9B]/8 blur-[140px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center w-full">
          <div className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.02] mb-12">
            <span className="w-1.5 h-1.5 rounded-full bg-[#29BC9B] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">
              Sistema de evolución bajo presión
            </span>
          </div>

          <h1
            className="font-display text-[clamp(3.5rem,12vw,10rem)] hero-gradient reveal mb-8"
            data-testid="hero-title"
          >
            PLAY YOUR<br />GAME<span className="text-[#29BC9B]">™</span>
          </h1>

          <p className="reveal text-xl md:text-3xl font-light text-white/70 max-w-3xl mx-auto mb-6">
            Descubre cómo juegas bajo presión.
          </p>

          <p className="reveal text-lg md:text-xl text-white/50 max-w-2xl mx-auto mb-14">
            Porque tu mayor fortaleza también puede convertirse en{" "}
            <span className="text-white italic">tu mayor problema.</span>
          </p>

          <div className="reveal flex flex-col items-center gap-5">
            <button
              data-testid="hero-cta-btn"
              onClick={() => setQuizOpen(true)}
              className="btn-teal !text-sm !py-5 !px-10"
            >
              Identificar mi forma de jugar <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/30 max-w-md text-center leading-relaxed">
              24 preguntas · ~4 minutos · resultado personalizado
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
        </div>
      </section>

      {/* AUTOMATIC STATES */}
      <section className="py-32 md:py-40 px-6 md:px-12 relative" data-testid="states-section">
        <div className="max-w-5xl mx-auto">
          <div className="reveal mb-16 md:mb-24 max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] mb-6">
              La respuesta automática
            </div>
            <h2 className="font-display text-4xl md:text-6xl text-white/90 leading-tight">
              Cuando hay presión, algo en ti{" "}
              <span className="text-white/40">decide antes que tú.</span>
            </h2>
          </div>

          <div className="space-y-2 md:space-y-4">
            {STATES.map((s, i) => (
              <div
                key={s}
                className="reveal group flex items-center gap-6 py-6 border-b border-white/5 hover:border-[#29BC9B]/40 transition-all duration-500"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="font-mono text-xs text-[#29BC9B]/60 tabular-nums w-8">
                  0{i + 1}
                </span>
                <span className="text-2xl md:text-4xl font-light text-white/30 group-hover:text-white transition-colors duration-500">
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TENSION QUOTE */}
      <section className="py-32 md:py-44 px-6 md:px-12 relative overflow-hidden border-y border-white/5">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1775206960600-42c7270ae29c"
            alt=""
            className="w-full h-full object-cover mix-blend-screen"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2
            className="font-display text-[clamp(2.2rem,6vw,5.5rem)] text-white reveal leading-[1.05]"
            data-testid="tension-quote"
          >
            La presión no cambia<br />
            <span className="text-white/40">cómo juegas.</span>
            <br />
            <span className="text-[#29BC9B]">Revela cómo juegas.</span>
          </h2>
          <div className="reveal mt-12 h-px w-20 bg-[#29BC9B]/40 mx-auto" />
          <p className="reveal mt-10 text-base md:text-lg font-light text-white/40 italic max-w-xl mx-auto">
            Tu fortaleza también puede jugar en tu contra.
          </p>
        </div>
      </section>

      {/* EL CAMINO */}
      <section
        id="camino"
        className="py-32 md:py-40 px-6 md:px-12 relative"
        data-testid="camino-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="reveal mb-20 md:mb-28 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] mb-5">
                Metodología
              </div>
              <h2 className="font-display text-5xl md:text-7xl text-white leading-none">
                EL CAMINO
              </h2>
            </div>
            <p className="text-white/50 max-w-md text-base md:text-lg leading-relaxed">
              Cuatro etapas para transformar tu reacción automática en una elección consciente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-white/5 border border-white/5 rounded-md overflow-hidden">
            {STAGES.map((s, i) => (
              <div
                key={s.n}
                className="tracing-card reveal p-10 md:p-14 group min-h-[300px] flex flex-col justify-between"
                data-testid={`stage-${s.n}`}
              >
                <div className="flex items-start justify-between mb-12">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#29BC9B] mb-3">
                      ETAPA {s.n}
                    </div>
                    <div className="text-xs uppercase tracking-[0.35em] text-white/40">
                      {s.label}
                    </div>
                  </div>
                  <s.Icon className="w-7 h-7 text-white/30 group-hover:text-[#29BC9B] transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-5 italic">
                    “{s.quote}”
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AVATARES */}
      <section
        id="avatares"
        className="py-32 md:py-40 px-6 md:px-12 relative"
        data-testid="avatares-section"
      >
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

        <div className="max-w-7xl mx-auto relative">
          <div className="reveal text-center mb-20 md:mb-24 max-w-3xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] mb-5">
              El sistema
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-6">
              Las 4 formas de jugar<br />
              <span className="text-white/40">bajo presión.</span>
            </h2>
            <p className="text-white/50 text-base md:text-lg font-light">
              Todos desarrollan una estrategia natural para reaccionar cuando hay
              presión, conflicto o incertidumbre.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 rounded-md overflow-hidden">
            {ARCHETYPES.map((a) => (
              <div
                key={a.code}
                data-testid={`avatar-${a.code}`}
                className="tracing-card group p-10 md:p-12 min-h-[340px] flex flex-col items-center text-center justify-center gap-8 reveal"
              >
                <div className="w-full flex justify-center">
                  <div
                    className="w-28 h-28 md:w-32 md:h-32 rounded-sm flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 relative"
                    style={{
                      background: `radial-gradient(circle, ${a.color}18, transparent 70%)`,
                    }}
                  >
                    <img
                      src={a.img}
                      alt={a.name}
                      className="w-20 h-20 md:w-24 md:h-24 object-contain transition-all duration-500"
                      style={{
                        filter:
                          "brightness(0) invert(1) drop-shadow(0 0 18px rgba(41,188,155,0.0))",
                      }}
                    />
                  </div>
                </div>
                <div>
                  <h4 className="font-display text-3xl md:text-4xl text-white mb-3 group-hover:text-[#29BC9B] transition-colors">
                    {a.name}
                  </h4>
                  <p className="text-sm md:text-base text-white/60 italic">
                    {a.tagline}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-12 border-y border-white/5 overflow-hidden" aria-hidden="true">
        <div className="marquee">
          {[0, 1].map((k) => (
            <div className="marquee-track" key={k}>
              {[
                "IDENTIFICACIÓN",
                "—",
                "CONCIENCIA",
                "—",
                "REGULACIÓN",
                "—",
                "EVOLUCIÓN",
                "—",
                "LEÓN",
                "—",
                "LOBO",
                "—",
                "HALCÓN",
                "—",
                "DELFÍN",
                "—",
              ].map((t, i) => (
                <span
                  key={i}
                  className={`font-display text-3xl md:text-5xl whitespace-nowrap ${
                    t === "—" ? "text-[#29BC9B]" : "text-white/20"
                  }`}
                >
                  {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FILOSOFÍA FINAL */}
      <section
        id="filosofia"
        className="py-32 md:py-44 px-6 md:px-12 relative overflow-hidden"
        data-testid="filosofia-section"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#29BC9B]/10 blur-[160px] rounded-full" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] mb-8">
            Filosofía
          </div>
          <h2 className="reveal font-display text-[clamp(2rem,5.5vw,5rem)] text-white leading-[1.05] mb-14">
            La madurez no es<br />
            cambiar quién eres.
            <br />
            <span className="text-[#29BC9B] italic">
              Es aprender a jugar mejor bajo presión.
            </span>
          </h2>

          <div className="reveal flex flex-col items-center gap-5">
            <button
              data-testid="final-cta-btn"
              onClick={() => setQuizOpen(true)}
              className="btn-teal !text-sm !py-6 !px-12"
            >
              Identificar mi forma de jugar <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/30">
              Tu evolución comienza con una pregunta.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 px-6 md:px-12 border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="font-display text-sm tracking-tighter text-white/60">
            PLAY YOUR GAME™
          </div>
          <p className="text-[10px] uppercase tracking-[0.4em] font-medium text-white/30 text-center">
            © 2024 — Sistema de evolución humana bajo presión
          </p>
          <div className="flex gap-6 text-[10px] uppercase tracking-[0.3em] text-white/30">
            <a href="#camino" className="hover:text-white transition-colors">El camino</a>
            <a href="#avatares" className="hover:text-white transition-colors">Avatares</a>
            <a href="/privacidad" className="hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>

      {/* QUIZ DIALOG */}
      <QuizDialog open={quizOpen} onOpenChange={setQuizOpen} />
    </div>
  );
}
